<script setup lang="ts">
    import { computed } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import ImageCarouselPicker from '@shared/components/Images/ImageCarouselPicker.vue';

    const props = defineProps<{
        imageUrls: string[];
        imageUrl: string | null;
        title: string | null;
        titleFallback: string | null;
        description: string | null;
        descriptionFallback: string | null;
        issueType: 'issue' | 'survey' | 'event';
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:imageUrl', newValue: string | null): void;
        (e: 'update:title', newValue: string): void;
        (e: 'update:description', newValue: string): void;
    }>();

    const infoText = computed(
        () =>
            ({
                issue: $Resources.IssueSocialShareSettingsText,
                survey: $Resources.SurveySocialShareSettingsText,
                event: $Resources.EventSocialShareSettingsText,
            })[props.issueType]
    );

    const descriptionPlaceholder = computed(() =>
        props.descriptionFallback ? props.descriptionFallback.substring(0, 277) + '...' : $Resources.SocialShareSettingsDescriptionPlaceholder
    );
</script>

<template>
    <div class="event-card">
        <h3 class="event-card-heading">
            {{ $Resources.SocialShareSettingsHeading }}
            <InfoPopoverButton :heading="$Resources.SocialShareSettingsHeading" :info-text="infoText" />
        </h3>

        <ImageCarouselPicker
            :imageUrls="imageUrls"
            :modelValue="imageUrl"
            :disabled="disabled"
            @update:modelValue="(newValue: string | null) => emit('update:imageUrl', newValue)" />

        <FormInput
            class="form-group"
            :label="$Resources.SocialShareSettingsTitleLabel"
            :modelValue="title ?? ''"
            :placeholder="titleFallback || $Resources.SocialShareSettingsTitlePlaceholder"
            :maxlength="255"
            :disabled="disabled"
            @update:modelValue="(newValue: string) => emit('update:title', newValue)" />

        <FormTextArea
            class="form-group"
            :label="$Resources.SocialShareSettingsDescriptionLabel"
            :modelValue="description ?? ''"
            :placeholder="descriptionPlaceholder"
            :maxlength="280"
            :rows="4"
            :disabled="disabled"
            @update:modelValue="(newValue: string) => emit('update:description', newValue)" />
    </div>
</template>
